/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import flattenDeep from 'lodash/flattenDeep';
import { createSelector } from 'reselect';

import { DEPENDENCY_TYPE_GRANULARITY, DEPENDENCY_TYPE_FUNCTION } from 'dataConstants';
import {
  GET_GRANULARITIES,
  GET_GRANULARITIES_SUCCESS,
  GET_GRANULARITIES_FAILURE,
  ADD_GRANULARITY,
  ADD_GRANULARITY_SUCCESS,
  ADD_GRANULARITY_FAILURE,
  REMOVE_GRANULARITY,
  REMOVE_GRANULARITY_SUCCESS,
  REMOVE_GRANULARITY_FAILURE,
  UPDATE_GRANULARITY,
  UPDATE_GRANULARITY_SUCCESS,
  UPDATE_GRANULARITY_FAILURE,
} from 'granularityActionCreators';
import { dataItemsSelector } from 'dataItemsReducer';

export const granularitiesReducer = (
  state = { data: {}, updatingData: false, fetchingData: false },
  action
) => {
  switch (action.type) {
    case GET_GRANULARITIES:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
        updateDataError: null,
      };
    case GET_GRANULARITIES_FAILURE:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_GRANULARITIES_SUCCESS:
    case ADD_GRANULARITY_SUCCESS:
    case REMOVE_GRANULARITY_SUCCESS:
    case UPDATE_GRANULARITY_SUCCESS:
      return {
        ...state,
        updatingData: false,
        fetchingData: false,
        fetchDataError: null,
        data: keyBy(action.payload, 'name'),
      };
    case UPDATE_GRANULARITY:
    case ADD_GRANULARITY:
    case REMOVE_GRANULARITY:
      return {
        ...state,
        updatingData: action.type,
        updateDataError: null,
      };
    case ADD_GRANULARITY_FAILURE:
    case REMOVE_GRANULARITY_FAILURE:
    case UPDATE_GRANULARITY_FAILURE:
      return {
        ...state,
        updatingData: false,
        updateDataError: action.payload,
      };
    default:
      return state;
  }
};

// simple selector for later memoization
export const selectGranularities = (state, excludeDashboards = false) => {
  const granularities = get(state, 'granularities.data', {});
  return excludeDashboards // leave out the summary dashboard granularities
    ? omitBy(granularities, { hasDashboardMeta: excludeDashboards })
    : granularities;
};

export const isSendingGranularity = state => get(state, 'granularities.updatingData');

export const getGranularities = createSelector([selectGranularities], granularities =>
  sortBy(Object.values(granularities) || [], 'default')
);

export const calculateDependencies = (existingKPIs, existingGranularities, dataItem) => {
  const filteredKPIs = flattenDeep(
    existingKPIs
      .filter(kpi => {
        const inputValueArrayOfArrays = Object.values(kpi.input).map(input =>
          !Array.isArray(input) ? [input] : input
        );
        return inputValueArrayOfArrays.find(inputArray => inputArray.includes(dataItem)); // Does the input array include the dataItem?
      })
      .map(kpi => [
        // return an array to support multiple output values, will flatten it above..
        Object.values(kpi.output).reduce((outputResult, outputValue) => {
          const outputArray = Array.isArray(outputValue) ? outputValue : outputValue.split(',');
          outputArray.forEach(value => {
            if (dataItem !== value) {
              // Do NOT include output in dependency list if same name as dataitem
              outputResult.push({
                name: value,
                type: DEPENDENCY_TYPE_FUNCTION,
              });
            }
          });
          return outputResult;
        }, []),
      ])
  );
  const filteredGranularities = existingGranularities
    .filter(granularity => granularity.dimensions && granularity.dimensions.includes(dataItem))
    .map(granularity => ({
      ...granularity,
      type: DEPENDENCY_TYPE_GRANULARITY,
    }));

  return [...filteredKPIs, ...filteredGranularities];
};

const selectKPIs = state => get(state, 'hierarchyLevelKPIs.data', {});

// TODO: may be can be removed
/**
 * Selector that returns a list of granularities and data items that are dependent on my current data item
 * @param {*} state
 * @param {*} dataItem the name of the dataItem to check
 */
export const getDependenciesOld = (state, dataItem) => {
  const existingGranularities = Object.values(selectGranularities(state));
  const existingKPIs = Object.values(selectKPIs(state));
  return calculateDependencies(existingKPIs, existingGranularities, dataItem);
};

/**
 * Selector that returns an object of dependencies keyed by dataItem name, where the value is an array of all the dependencies on that data item
 * @param {*} state
 */
export const getAllDependencies = createSelector(
  [dataItemsSelector, selectKPIs, selectGranularities],
  (dataItems, kpis, granularities) =>
    isEmpty(dataItems)
      ? {}
      : keyBy(
          dataItems.map(dataItem => ({
            uuid: dataItem.uuid,
            name: dataItem.name,
            dependencies: calculateDependencies(
              Object.values(kpis),
              Object.values(granularities),
              dataItem.name
            ),
          })),
          'uuid'
        )
);
